import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";

import {
  createTournamentTestimonial,
  listTournamentTestimonials,
  listVideoGlimpses,
  TournamentTestimonial,
} from "../../../api/tournament/contentService";

type TournamentContentTabProps = {
  tournamentId: string;
  sport: string;
  canManage: boolean;
};

const asList = (response: any, keys: string[]) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  return keys.flatMap((key) => Array.isArray(data?.[key]) ? [data[key]] : [])[0] || [];
};

const initialForm: TournamentTestimonial = {
  player: "",
  desc: "",
  type: "text",
  link: "",
  thumbnail: "",
};

const TournamentContentTab: React.FC<TournamentContentTabProps> = ({ tournamentId, sport, canManage }) => {
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [glimpses, setGlimpses] = useState<any[]>([]);
  const [form, setForm] = useState<TournamentTestimonial>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [testimonialResponse, glimpseResponse] = await Promise.all([
        listTournamentTestimonials(tournamentId, sport),
        listVideoGlimpses(tournamentId, sport),
      ]);
      setTestimonials(asList(testimonialResponse, ["testimonials", "results", "paginatedResults"]));
      setGlimpses(asList(glimpseResponse, ["videoGlimpses", "glimpses", "results"]));
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || "Unable to load tournament content.");
    } finally {
      setLoading(false);
    }
  }, [sport, tournamentId]);

  useEffect(() => { load(); }, [load]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const player = form.player.trim();
    const desc = form.desc?.trim();
    const link = form.link?.trim();
    if (!player || (form.type === "text" && !desc) || (form.type === "video" && !link)) {
      setError(form.type === "video"
        ? "Player ID and video link are required."
        : "Player ID and testimonial text are required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createTournamentTestimonial(tournamentId, sport, {
        player,
        type: form.type,
        ...(form.type === "text" ? { desc } : { link, ...(form.thumbnail?.trim() ? { thumbnail: form.thumbnail.trim() } : {}) }),
      });
      setForm(initialForm);
      await load();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || "Unable to save testimonial.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row className="g-4">
      {canManage && <Col lg={5}>
        <Card>
          <Card.Header><h5 className="mb-0">Add testimonial</h5></Card.Header>
          <Card.Body>
            <Form onSubmit={save}>
              <Form.Group className="mb-3">
                <Form.Label>Player ID</Form.Label>
                <Form.Control required value={form.player} onChange={(event) => setForm({ ...form, player: event.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as TournamentTestimonial["type"], desc: "", link: "", thumbnail: "" })}>
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                </Form.Select>
              </Form.Group>
              {form.type === "text" ? (
                <Form.Group className="mb-3">
                  <Form.Label>Testimonial</Form.Label>
                  <Form.Control required as="textarea" rows={4} value={form.desc || ""} onChange={(event) => setForm({ ...form, desc: event.target.value })} />
                </Form.Group>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Video link</Form.Label>
                    <Form.Control required type="url" value={form.link || ""} onChange={(event) => setForm({ ...form, link: event.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Thumbnail URL</Form.Label>
                    <Form.Control type="url" value={form.thumbnail || ""} onChange={(event) => setForm({ ...form, thumbnail: event.target.value })} />
                  </Form.Group>
                </>
              )}
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add testimonial"}</Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>}
      <Col lg={canManage ? 7 : 12}>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center"><h5 className="mb-0">Testimonials</h5><Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Refresh</Button></Card.Header>
          <Card.Body className="p-0">
            {loading ? <div className="text-center py-4"><Spinner size="sm" className="me-2" />Loading…</div> : testimonials.length ? (
              <Table responsive className="mb-0"><thead><tr><th>Player</th><th>Type</th><th>Content</th></tr></thead><tbody>
                {testimonials.map((testimonial, index) => <tr key={testimonial._id || index}><td>{testimonial.player?.name || testimonial.playerName || testimonial.player || "—"}</td><td><Badge bg="secondary">{testimonial.type || "text"}</Badge></td><td>{testimonial.desc || testimonial.link || "—"}</td></tr>)}
              </tbody></Table>
            ) : <div className="text-center text-muted py-4">No testimonials yet.</div>}
          </Card.Body>
        </Card>
        <Card>
          <Card.Header><h5 className="mb-0">Video glimpses</h5></Card.Header>
          <Card.Body className="p-0">
            {loading ? <div className="text-center py-4">Loading…</div> : glimpses.length ? (
              <Table responsive className="mb-0"><thead><tr><th>Title</th><th>Video</th></tr></thead><tbody>
                {glimpses.map((glimpse, index) => <tr key={glimpse._id || index}><td>{glimpse.title || glimpse.name || "—"}</td><td>{glimpse.link || glimpse.videoUrl || glimpse.url || "—"}</td></tr>)}
              </tbody></Table>
            ) : <div className="text-center text-muted py-4">No video glimpses are available.</div>}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default TournamentContentTab;
